import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useEffect } from "react";
import { usePageTracking } from "@/hooks/usePageTracking";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import SignUp from "./pages/SignUp";
import SignIn from "./pages/SignIn";
import PaymentGatePage from "./pages/PaymentGatePage";
import Dashboard from "./pages/Dashboard";
import DashboardProfile from "./pages/DashboardProfile";
import DashboardProgress from "./pages/DashboardProgress";
import DashboardResources from "./pages/DashboardResources";
import DashboardCommunity from "./pages/DashboardCommunity";
import DashboardDiscussions from "./pages/DashboardDiscussions";
import CaseStudies from "./pages/CaseStudies";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import About from "./pages/About";
import FullCurriculum from "./pages/FullCurriculum";
import Analytics from "./pages/Analytics";
import MobileApp from "./pages/MobileApp";
import DashboardSchedule from "./pages/DashboardSchedule";
import DashboardEnergy from "./pages/DashboardEnergy";
import DashboardGoals from "./pages/DashboardGoals";
import DashboardMoodBoard from "./pages/DashboardMoodBoard";
import LaunchCalendar from "./pages/LaunchCalendar";
import AdminManagement from "./pages/AdminManagement";
import AdminPartners from "./pages/AdminPartners";
import AdminPartnerInquiries from "./pages/AdminPartnerInquiries";
import DashboardContentPlanner from "./pages/DashboardContentPlanner";
import PaymentSuccess from "./pages/PaymentSuccess";
import DiscountCodes from "./pages/DiscountCodes";
import NotFound from "./pages/NotFound";
import CreateTestUsers from "./pages/CreateTestUsers";
import AdminInvites from "./pages/AdminInvites";
import DashboardStats from "./pages/DashboardStats";
import DashboardHabits from "./pages/DashboardHabits";
import DashboardLeaderboard from "./pages/DashboardLeaderboard";
import DashboardAchievements from "./pages/DashboardAchievements";
import DashboardStudyGroups from "./pages/DashboardStudyGroups";
import DashboardReferrals from "./pages/DashboardReferrals";
import AdminReferrals from "./pages/AdminReferrals";
import DashboardInvoices from "./pages/DashboardInvoices";
import DashboardSupport from "./pages/DashboardSupport";
import DashboardSupportTicketDetail from "./pages/DashboardSupportTicketDetail";
import DashboardSessions from "./pages/DashboardSessions";
import DashboardSessionDetail from "./pages/DashboardSessionDetail";
import Welcome from "./pages/Welcome";
import DashboardMoodTracker from "./pages/DashboardMoodTracker";
import ConsultationRequestDetail from "./pages/ConsultationRequestDetail";
import AdminPricing from "./pages/AdminPricing";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import AffiliateEmailAutomation from "./pages/AffiliateEmailAutomation";
import AdminEmailTemplates from "./pages/AdminEmailTemplates";
import AdminInvoices from "./pages/AdminInvoices";
import DashboardBookmarks from "./pages/DashboardBookmarks";
import DashboardNotes from "./pages/DashboardNotes";
import DashboardTasks from "./pages/DashboardTasks";
import DashboardFocus from "./pages/DashboardFocus";
import DashboardSettings from "./pages/DashboardSettings";
import DashboardCheckin from "./pages/DashboardCheckin";
import DashboardWeeklyReview from "./pages/DashboardWeeklyReview";
import AdminMentorship from "./pages/AdminMentorship";
import CommunityCalendar from "./pages/CommunityCalendar";
import AdminReports from "./pages/AdminReports";
import AdminCallRequests from "./pages/AdminCallRequests";
import DashboardMessages from "./pages/DashboardMessages";
import DashboardAvatarGenerator from "./pages/DashboardAvatarGenerator";
import DashboardPartner from "./pages/DashboardPartner";
import AdminAffiliates from "./pages/AdminAffiliates";
import AdminAnnouncements from "./pages/AdminAnnouncements";
import Partner from "./pages/Partner";
import JoinToday from "./pages/JoinToday";
import Pricing from "./pages/Pricing";
import AcceptInvite from "./pages/AcceptInvite";
import { PaymentGate } from "./components/PaymentGate";

const queryClient = new QueryClient();

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // More aggressive scroll reset
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Additional reset after content loads
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 100);
    
    return () => clearTimeout(timer);
  }, [pathname]);

  return null;
}

function PageTracking() {
  usePageTracking();
  return null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <PageTracking />
          <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/get-started" element={<JoinToday />} />
          <Route path="/join-today" element={<JoinToday />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/curriculum" element={<FullCurriculum />} />
          <Route path="/case-studies" element={<CaseStudies />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/about" element={<About />} />
          <Route path="/partner" element={<Partner />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/sign-up" element={<SignUp />} />
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/payment-gate" element={<PaymentGatePage />} />
          <Route path="/dashboard" element={<PaymentGate><Dashboard /></PaymentGate>} />
          <Route path="/dashboard/profile" element={<PaymentGate><DashboardProfile /></PaymentGate>} />
          <Route path="/dashboard/progress" element={<PaymentGate><DashboardProgress /></PaymentGate>} />
          <Route path="/dashboard/resources" element={<PaymentGate><DashboardResources /></PaymentGate>} />
          <Route path="/dashboard/community" element={<PaymentGate><DashboardCommunity /></PaymentGate>} />
          <Route path="/dashboard/discussions" element={<PaymentGate><DashboardDiscussions /></PaymentGate>} />
          <Route path="/dashboard/analytics" element={<PaymentGate><Analytics /></PaymentGate>} />
          <Route path="/dashboard/schedule" element={<PaymentGate><DashboardSchedule /></PaymentGate>} />
          <Route path="/dashboard/energy" element={<PaymentGate><DashboardEnergy /></PaymentGate>} />
          <Route path="/dashboard/goals" element={<PaymentGate><DashboardGoals /></PaymentGate>} />
          <Route path="/dashboard/moodboard" element={<PaymentGate><DashboardMoodBoard /></PaymentGate>} />
          <Route path="/dashboard/mood-tracker" element={<PaymentGate><DashboardMoodTracker /></PaymentGate>} />
          <Route path="/dashboard/calendar" element={<PaymentGate><LaunchCalendar /></PaymentGate>} />
          <Route path="/dashboard/content-planner" element={<PaymentGate><DashboardContentPlanner /></PaymentGate>} />
           <Route path="/dashboard/admin" element={<PaymentGate><AdminManagement /></PaymentGate>} />
           <Route path="/admin" element={<PaymentGate><AdminManagement /></PaymentGate>} />
           <Route path="/admin/partners" element={<PaymentGate><AdminPartners /></PaymentGate>} />
          <Route path="/admin/partner-inquiries" element={<PaymentGate><AdminPartnerInquiries /></PaymentGate>} />
          <Route path="/dashboard/stats" element={<PaymentGate><DashboardStats /></PaymentGate>} />
          <Route path="/dashboard/habits" element={<PaymentGate><DashboardHabits /></PaymentGate>} />
          <Route path="/dashboard/leaderboard" element={<PaymentGate><DashboardLeaderboard /></PaymentGate>} />
          <Route path="/dashboard/achievements" element={<PaymentGate><DashboardAchievements /></PaymentGate>} />
          <Route path="/dashboard/study-groups" element={<PaymentGate><DashboardStudyGroups /></PaymentGate>} />
          <Route path="/dashboard/referrals" element={<PaymentGate><DashboardReferrals /></PaymentGate>} />
          <Route path="/dashboard/invoices" element={<PaymentGate><DashboardInvoices /></PaymentGate>} />
          <Route path="/dashboard/support" element={<PaymentGate><DashboardSupport /></PaymentGate>} />
          <Route path="/dashboard/support/:ticketId" element={<PaymentGate><DashboardSupportTicketDetail /></PaymentGate>} />
          <Route path="/dashboard/sessions" element={<PaymentGate><DashboardSessions /></PaymentGate>} />
          <Route path="/dashboard/sessions/:sessionId" element={<PaymentGate><DashboardSessionDetail /></PaymentGate>} />
          <Route path="/dashboard/bookmarks" element={<PaymentGate><DashboardBookmarks /></PaymentGate>} />
          <Route path="/dashboard/notes" element={<PaymentGate><DashboardNotes /></PaymentGate>} />
          <Route path="/dashboard/tasks" element={<PaymentGate><DashboardTasks /></PaymentGate>} />
          <Route path="/dashboard/focus" element={<PaymentGate><DashboardFocus /></PaymentGate>} />
          <Route path="/dashboard/settings" element={<PaymentGate><DashboardSettings /></PaymentGate>} />
          <Route path="/dashboard/checkin" element={<PaymentGate><DashboardCheckin /></PaymentGate>} />
          <Route path="/dashboard/weekly-review" element={<PaymentGate><DashboardWeeklyReview /></PaymentGate>} />
          <Route path="/consultation-request/:id" element={<PaymentGate><ConsultationRequestDetail /></PaymentGate>} />
          <Route path="/admin/management" element={<PaymentGate><AdminManagement /></PaymentGate>} />
          <Route path="/admin/pricing" element={<PaymentGate><AdminPricing /></PaymentGate>} />
          <Route path="/admin/referrals" element={<PaymentGate><AdminReferrals /></PaymentGate>} />
           <Route path="/admin/invoices" element={<PaymentGate><AdminInvoices /></PaymentGate>} />
           <Route path="/admin/invites" element={<PaymentGate><AdminInvites /></PaymentGate>} />
           <Route path="/admin/email-templates" element={<PaymentGate><AdminEmailTemplates /></PaymentGate>} />
          <Route path="/affiliate-email-automation" element={<PaymentGate><AffiliateEmailAutomation /></PaymentGate>} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/discount-codes" element={<DiscountCodes />} />
          <Route path="/mobile-app" element={<MobileApp />} />
          <Route path="/welcome" element={<Welcome />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/create-test-users" element={<CreateTestUsers />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/admin/mentorship" element={<PaymentGate><AdminMentorship /></PaymentGate>} />
          <Route path="/admin/announcements" element={<PaymentGate><AdminAnnouncements /></PaymentGate>} />
          <Route path="/community/calendar" element={<PaymentGate><CommunityCalendar /></PaymentGate>} />
           <Route path="/admin/reports" element={<PaymentGate><AdminReports /></PaymentGate>} />
            <Route path="/admin/call-requests" element={<PaymentGate><AdminCallRequests /></PaymentGate>} />
            <Route path="/admin/affiliates" element={<PaymentGate><AdminAffiliates /></PaymentGate>} />
            <Route path="/dashboard/messages" element={<PaymentGate><DashboardMessages /></PaymentGate>} />
            <Route path="/dashboard/avatar-generator" element={<PaymentGate><DashboardAvatarGenerator /></PaymentGate>} />
            <Route path="/dashboard/partner" element={<PaymentGate><DashboardPartner /></PaymentGate>} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
           <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
